import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { checkActionSubPermission, toTypeCase } from '../../../../utils/common';
import MlxActionIcon from '../../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../../components/common/MlxInputSwitch/MlxInputSwitch';
import { QueryClient, useMutation, useQuery, UseQueryResult } from 'react-query';
import { ADMIN_STAFF_LIST } from '../../../../constants/queryKeys';
import { _AdminStaff, _DatatablePageEvent, _Pagination, _ToastMessageProps } from '../../../../constants/staticTypes';
import { defaultPaginationEvent } from '../../../../helpers/commonHelper';
import logger from '../../../../utils/logger';
import {
    _AddAdminStaffFormType,
    deleteAdminStaffDetailByID,
    getAdminStaffDetailByID,
    getAdminStaffList,
    updateAdminStaffStatus,
} from '../../../../helpers/adiminStaffHelper';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxConfirmPopup from '../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { confirmPopup } from 'primereact/confirmpopup';
import { CONFIRM_ADMIN_STAFF_DELETE, CONFIRM_ADMIN_STAFF_STATUS_CHANGE } from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import { AlertVariant, PermissionKeys, SubPermissionKeys } from '../../../../constants/constants';
import { AuthCtx } from '../../../../context/AuthCtxProvider';
import { AxiosResponse } from 'axios';
import MlxToolTip from '../../../../components/common/MlxToolTip/MlxToolTip';

type Props = {
    dataTableEvent: _DatatablePageEvent;
    setDataTableEvent: Dispatch<SetStateAction<_DatatablePageEvent>>;
    openEditAdminStaffSidebar: (data: _AddAdminStaffFormType) => void;
    adminStaffListData: _AdminStaff[];
    setAdminStaffListData: Dispatch<SetStateAction<_AdminStaff[]>>;
    adminStaffListQuery: UseQueryResult<AxiosResponse<any, any>, unknown>;
    setToastMessage?: (toastMessage: _ToastMessageProps) => void;
};

const languageTexts = {
    CONFIRM_ADMIN_STAFF_DELETE: CONFIRM_ADMIN_STAFF_DELETE,
    CONFIRM_ADMIN_STAFF_STATUS_CHANGE: CONFIRM_ADMIN_STAFF_STATUS_CHANGE,
};

function AdminStaffList({
    dataTableEvent,
    setDataTableEvent,
    openEditAdminStaffSidebar,
    adminStaffListData,
    setAdminStaffListData,
    adminStaffListQuery,
    setToastMessage,
}: Readonly<Props>) {
    const queryClient = new QueryClient();
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const getAdminStaffByIDMutation = useMutation(HttpServiceHelper);
    const { userType, userData } = useContext(AuthCtx);

    const handleAdminStaffStatusChange = (rowData: any, event: any) => {
        updateAdminStaffStatus(
            getAdminStaffByIDMutation,
            { id: rowData?.id, newStatus: !event.target.checked },
            (data) => {
                // Update the isActive field in the state
                setAdminStaffListData((prevData: any) =>
                    prevData.map((staff: any) => (staff.id === rowData.id ? { ...staff, isActive: !staff.isActive } : staff))
                );
            },
            (error) => logger.log('Failed to update the status', error)
        );
    };

    const confirmStatusChange = (event: any, rowData: any) => {
        confirmPopup({
            target: event.target,
            message: translatedText.CONFIRM_ADMIN_STAFF_STATUS_CHANGE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleAdminStaffStatusChange(rowData, event),
            reject: () => {},
        });
    };

    const statusTemplateWithAction = (rowData: any) => {
        // Disable switch if current user is adminStaff and row user is superAdmin, or if the row is the logged-in user
        const isSelf = rowData.id === userData?.id;
        const isDisabled = (userType === 'adminStaff' && rowData.userType === 'superAdmin') || isSelf;

        const handleStatusClick = (event: any) => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: isSelf ? 'You cannot change your own status' : 'You cannot modify a Super Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                confirmStatusChange(event, rowData);
            }
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{toTypeCase(rowData.isActive ? 'Active' : 'Inactive', 'capitalize')}</span>
                <div
                    style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    onClick={(e) => {
                        if (isDisabled) {
                            e.stopPropagation();
                            handleStatusClick(e);
                        }
                    }}
                >
                    <MlxInputSwitch
                        name="admin-staff-status"
                        checked={rowData.isActive}
                        onClick={!isDisabled ? handleStatusClick : undefined}
                    />
                </div>
            </div>
        );
    };

    const handleAdminStaffEdit = (rowData: any) => {
        getAdminStaffDetailByID(
            getAdminStaffByIDMutation,
            { id: rowData?.id },
            (data) => {
                let adminStaffData = data?.data;
                const adminStaffEditableDataForm: _AddAdminStaffFormType = {
                    id: adminStaffData.id,
                    loginUserName: adminStaffData.loginUserName,
                    password: '',
                    preferredName: adminStaffData.preferredName,
                    firstName: adminStaffData.firstName,
                    lastName: adminStaffData.lastName,
                    email: adminStaffData.email,
                    phoneNumber: adminStaffData.phoneNumber,
                    hireDate: adminStaffData.hireDate,
                    staffPermissionRoles: adminStaffData.staffPermissionRoles[0]?.permissionRoleId,
                    organizations: adminStaffData.organizations.map((item: any) => {
                        return {
                            organization: {
                                id: item.organizationId,
                                organizationName: item.organizationName,
                            },
                            permissionGroup: {
                                id: item.staffPermissionRoles[0]?.permissionRoleId,
                                permissionRoleName: item.staffPermissionRoles[0]?.adminOrganizationPermissionRoleName,
                            },
                        };
                    }),
                    photoKey: adminStaffData.photoKey,
                    sendPasswordCreation: true,
                    passwordLessLoginOnly: true,
                    sendPasswordCreationTo: 'email',
                    userType: adminStaffData.userType,
                    signedUrl: adminStaffData.signedUrl,
                };
                openEditAdminStaffSidebar(adminStaffEditableDataForm);
            },
            (error) => logger.log('Failed to fetch user details', error)
        );
    };

    const confirmDelete = (event: any, rowData: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: translatedText.CONFIRM_ADMIN_STAFF_DELETE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleAdminStaffDelete(rowData),
            reject: () => {},
        });
    };

    const handleAdminStaffDelete = (rowData: any) => {
        deleteAdminStaffDetailByID(
            getAdminStaffByIDMutation,
            { id: rowData?.id },
            () => {
                adminStaffListQuery.refetch();
            },
            (error) => logger.log('Failed to delete admin staff', error)
        );
    };

    const actionTemplate = (rowData: any) => {
        // Disable actions if current user is adminStaff and row user is superAdmin
        const isDisabled = userType === 'adminStaff' && rowData.userType === 'superAdmin';

        const handleEditClick = () => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: 'You cannot edit a Super Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                handleAdminStaffEdit(rowData);
            }
        };

        const handleDeleteClick = (event: any) => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: 'You cannot delete a Super Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                confirmDelete(event, rowData);
            }
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.EDIT_STAFF) ? (
                    <span className={isDisabled ? 'opacity-50' : ''} onClick={isDisabled ? handleEditClick : undefined}>
                        <MlxActionIcon
                            className="me-3 edit-icon-selection"
                            actionType="EDIT"
                            onClick={!isDisabled ? handleEditClick : undefined}
                        />
                    </span>
                ) : (
                    <></>
                )}
                {userType === 'superAdmin' ||
                checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.DELETE_STAFF) ? (
                    <span className={isDisabled ? 'opacity-50' : ''} onClick={isDisabled ? handleDeleteClick : undefined}>
                        <MlxActionIcon
                            className="me-3 delete-icon-selection"
                            actionType="DELETE"
                            onClick={!isDisabled ? handleDeleteClick : undefined}
                        />
                    </span>
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return (
        <MlxBlockUI isLoading={getAdminStaffByIDMutation.isLoading || adminStaffListQuery.isRefetching} opacity={0.5}>
            {/* <MlxConfirmPopup className="list" /> */}
            <DataTable
                className="custom-p-table"
                value={adminStaffListData}
                paginator
                rows={dataTableEvent.rows}
                totalRecords={dataTableEvent.totalRows}
                first={dataTableEvent.first}
                rowsPerPageOptions={dataTableEvent.rowsPerPageOptions}
                size="small"
                removableSort
                scrollable
                scrollHeight="flex"
                paginatorTemplate={dataTableEvent.paginatorTemplate}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                dataKey="id"
                onPage={(event) => setDataTableEvent({ ...dataTableEvent, ...event, forceUpdate: dataTableEvent.forceUpdate + 1 })}
                lazy
            >
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="First Name" field="firstName"></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Last Name" field="lastName"></Column>
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    field="email"
                    header="Email"
                    body={(rowData) => (
                        <MlxToolTip target={`.staffEmail-${rowData.id}`}>
                            <div
                                className={`staffEmail-${rowData.id} overflow-ellipsis`}
                                data-pr-tooltip={rowData?.email}
                                data-pr-position="bottom"
                            >
                                {rowData?.email}
                            </div>
                        </MlxToolTip>
                    )}
                ></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} field="phoneNumber" header="Phone"></Column>
                {/* <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Organization" body={(rowData) => rowData.org.name}></Column> */}
                <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplateWithAction}></Column>
                <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>
            </DataTable>
        </MlxBlockUI>
    );
}

export default AdminStaffList;
